/**
 * Subscriptions Hook
 *
 * Provides data fetching and mutations for subscriptions
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { reportingService } from '@/services/reporting.service';
import toast from 'react-hot-toast';
import type {
  ListSubscriptionsQuery,
  PaginatedResponse,
  Subscription,
  CreateSubscriptionRequest,
  DeliveryHistoryQuery,
  DeliveryHistoryResponse,
} from '@/types/reporting-v2';

export function useSubscriptions(
  query: ListSubscriptionsQuery,
  options?: Omit<UseQueryOptions<PaginatedResponse<Subscription>['data']>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['subscriptions', 'list', query],
    queryFn: () => reportingService.listSubscriptions(query),
    staleTime: 60 * 1000, // 1 minute
    ...options,
  });
}

export function useSubscription(
  subscriptionId: string | undefined,
  options?: Omit<UseQueryOptions<Subscription>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['subscriptions', subscriptionId],
    queryFn: () => reportingService.getSubscription(subscriptionId!),
    enabled: !!subscriptionId,
    staleTime: 60 * 1000, // 1 minute
    ...options,
  });
}

export function useCreateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreateSubscriptionRequest) => reportingService.createSubscription(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions', 'list'] });
      toast.success('Subscription created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create subscription');
    },
  });
}

export function useUpdateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ subscriptionId, params }: { subscriptionId: string; params: Partial<CreateSubscriptionRequest> }) =>
      reportingService.updateSubscription(subscriptionId, params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions', variables.subscriptionId] });
      queryClient.invalidateQueries({ queryKey: ['subscriptions', 'list'] });
      toast.success('Subscription updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update subscription');
    },
  });
}

export function useDeleteSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (subscriptionId: string) => reportingService.deleteSubscription(subscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions', 'list'] });
      toast.success('Subscription deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete subscription');
    },
  });
}

export function useSendSubscriptionNow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ subscriptionId, params }: { subscriptionId: string; params?: any }) =>
      reportingService.sendSubscriptionNow(subscriptionId, params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions', variables.subscriptionId] });
      toast.success('Subscription sent successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to send subscription');
    },
  });
}

export function usePauseSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (subscriptionId: string) => reportingService.pauseSubscription(subscriptionId),
    onSuccess: (_, subscriptionId) => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions', subscriptionId] });
      queryClient.invalidateQueries({ queryKey: ['subscriptions', 'list'] });
      toast.success('Subscription paused');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to pause subscription');
    },
  });
}

export function useResumeSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (subscriptionId: string) => reportingService.resumeSubscription(subscriptionId),
    onSuccess: (_, subscriptionId) => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions', subscriptionId] });
      queryClient.invalidateQueries({ queryKey: ['subscriptions', 'list'] });
      toast.success('Subscription resumed');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to resume subscription');
    },
  });
}

export function useDeliveryHistory(
  subscriptionId: string | undefined,
  query: DeliveryHistoryQuery,
  options?: Omit<UseQueryOptions<DeliveryHistoryResponse['data']>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['subscriptions', subscriptionId, 'deliveries', query],
    queryFn: () => reportingService.getDeliveryHistory(subscriptionId!, query),
    enabled: !!subscriptionId,
    staleTime: 30 * 1000, // 30 seconds
    ...options,
  });
}
